import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CadastroStep1() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", password: "", gender: "Feminino",
    zip_code: "", street: "", number: "", city: "", state: "",
  });
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.full_name.trim()) err.full_name = "Informe seu nome completo";
    if (!form.phone.trim()) err.phone = "Informe seu celular";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) err.email = "E-mail inválido";
    if (form.password.length < 6) err.password = "Mínimo 6 caracteres";
    if (!form.zip_code.trim()) err.zip_code = "Informe o CEP";
    if (!form.street.trim()) err.street = "Informe a rua";
    if (!form.number.trim()) err.number = "Nº";
    if (!form.city.trim()) err.city = "Informe a cidade";
    if (!form.state.trim()) err.state = "UF";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/onboarding/step-1", { ...form, privacy_accepted: accepted });
      await refreshMe();
      toast.success("Dados salvos! Continue seu cadastro.");
      navigate("/cadastro/personalidade");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mc-container py-14 flex justify-center" data-testid="page-cadastro-step1">
      <div className="mc-card w-full max-w-lg p-8 mc-fade-up">
        <h1 className="text-3xl font-bold text-center text-[#1e1b2e]">Crie sua conta</h1>
        <p className="text-center text-muted-foreground mt-2 text-sm">Cadastre-se para encontrar conexões seguras e compatíveis.</p>

        <div className="grid grid-cols-2 gap-2 bg-[#f4f2fb] p-1 rounded-full mt-6">
          <button className="rounded-full py-2 text-sm font-semibold text-muted-foreground" data-testid="tab-entrar" onClick={() => navigate("/entrar")}>Entrar</button>
          <button className="rounded-full py-2 text-sm font-semibold text-white mc-grad" data-testid="tab-registrar">Registrar</button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
          <F label="Nome Completo" err={errors.full_name}>
            <input className="mc-input" data-testid="cad-name" placeholder="Digite seu nome completo" value={form.full_name} onChange={set("full_name")} />
          </F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Número de celular" err={errors.phone}>
              <input className="mc-input" data-testid="cad-phone" placeholder="(43) 9 8765-4321" value={form.phone} onChange={set("phone")} />
            </F>
            <F label="E-mail" err={errors.email}>
              <input className="mc-input" data-testid="cad-email" placeholder="Digite seu melhor e-mail" value={form.email} onChange={set("email")} />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Senha" err={errors.password}>
              <input className="mc-input" type="password" data-testid="cad-password" placeholder="Crie uma senha" value={form.password} onChange={set("password")} />
            </F>
            <F label="Sexo">
              <select className="mc-input" data-testid="cad-gender" value={form.gender} onChange={set("gender")}>
                <option>Feminino</option><option>Masculino</option><option>Outro</option><option>Prefiro não informar</option>
              </select>
            </F>
          </div>
          <F label="CEP" err={errors.zip_code}>
            <input className="mc-input" data-testid="cad-cep" placeholder="00000-000" value={form.zip_code} onChange={set("zip_code")} />
          </F>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2"><F label="Rua" err={errors.street}><input className="mc-input" data-testid="cad-street" placeholder="Rua das Flores" value={form.street} onChange={set("street")} /></F></div>
            <F label="Número" err={errors.number}><input className="mc-input" data-testid="cad-number" placeholder="123" value={form.number} onChange={set("number")} /></F>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2"><F label="Cidade" err={errors.city}><input className="mc-input" data-testid="cad-city" placeholder="São Paulo" value={form.city} onChange={set("city")} /></F></div>
            <F label="Estado" err={errors.state}><input className="mc-input" data-testid="cad-state" placeholder="SP" value={form.state} onChange={set("state")} /></F>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#1e1b2e] cursor-pointer">
            <input type="checkbox" data-testid="cad-privacy" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="accent-violet-600 w-4 h-4" />
            Concordo com a <span className="text-violet-600 font-semibold">Política de Privacidade</span>
          </label>
          <button className="btn-primary w-full disabled:!bg-none disabled:!bg-[#e9e7f1] disabled:!text-[#9a95ad]" data-testid="cad-next" disabled={!accepted || loading}>
            {loading ? "Salvando..." : "Próximo"}
          </button>
        </form>
      </div>
    </div>
  );
}

function F({ label, err, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#1e1b2e] mb-2">{label}</label>
      {children}
      {err && <p className="text-destructive text-xs mt-1">{err}</p>}
    </div>
  );
}
